import PropTypes from 'prop-types';
import { useCallback } from 'react';

import {
  Button, DateTimeFormat, Icon, IconUrgency, Span,
  StatusDocument
} from '@febos-io/design';
import { providers } from '@febos-io/common';

import useTranslation from '../hooks/useTranslation';

export function PatientExamRow({
  id,
  number,
  status,
  dentist,
  technician,
  radiologist,
  medicalCenter,
  medicalDiagnosis,
  patient,
  urgency,
  createdAt,
  diagnostic,
  observations,
  name,
  companyName,
  case: caseAntecedent
}) {
  const { t } = useTranslation();
  const { openModal } = providers.useModalContext();
  const openModalExam = useCallback(
    () => {
      openModal('modal-patient-exam', {
        id: id,
        code: number
      });
    },
    [id, number, openModal]
  );

  return (
    <div
      key={`card-${id}`}
      className="card border mb-2 rounded"
    >
      <div className="card-body">
        <div className="row">
          <div className="col-md-9 border-end">
            <div className="row justify-content-between pb-1 border-bottom">
              <div className="col-sm-7">
                <IconUrgency
                  urgency={urgency}
                  icon="flag"
                />
                <strong className="text-uppercase text-primary ms-1">{t(name)}</strong>
              </div>
              <div className="col-sm-5 text-md-end">
                <div className="row">
                  <div className="col">
                    <Span title={t('Creation Date')}>
                      <Icon
                        icon="calendar-day"
                        className="me-1"
                      />
                      <DateTimeFormat
                        value={createdAt}
                        format="dd-MM-yyyy hh:mm"
                      />
                    </Span>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Span
                      title={t('Exam Code')}
                    >
                      <Icon
                        icon="rectangle-barcode"
                        className="me-1"
                      />
                      {number}
                    </Span>
                    {(caseAntecedent) && (
                      <Span
                        title={`${t('Associated with clinical study')} #${caseAntecedent?.number}`}
                        className="badge bg-gray-500 ms-1"
                      >
                        <Icon
                          icon="memo-circle-info"
                          className="me-1"
                        />
                        {caseAntecedent?.number}
                      </Span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="row mt-1">
              <div className="col-md-6">
                <div className="mb-1">
                  <strong className="text-gray">{t('Patient')}</strong>
                  <br />
                  <Icon
                    icon="user"
                    className="me-2"
                  />
                  {patient.name}
                  {' '}
                  {patient.lastName}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-1">
                  <strong className="text-gray">{t('Medical Center')}</strong>
                  <br />
                  <Icon
                    icon="hospital"
                    className="me-1"
                  />
                  {medicalCenter?.name}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-1">
                  <strong className="text-gray">{t('Dentist')}</strong>
                  <br />
                  <Icon
                    icon="user-doctor"
                    className="me-2"
                  />
                  {'Dr(a). '}
                  {dentist?.name}
                  {' '}
                  {dentist?.lastName}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-1">
                  <strong className="text-gray">{t('Radiology Technician')}</strong>
                  <br />
                  <Icon
                    icon="users-medical"
                    className="me-1"
                  />
                  {technician?.name}
                </div>
              </div>
              {radiologist && (
                <div className="col-md-6">
                  <div className="mb-1">
                    <strong className="text-gray">{t('Radiologist')}</strong>
                    <br />
                    <Icon
                      icon="users-medical"
                      className="me-1"
                    />
                    {radiologist?.name}
                  </div>
                </div>
              )}
              {companyName && (
                <div className="col-md-6">
                  <div className="mb-1">
                    <strong className="text-gray">{t('Company')}</strong>
                    <br />
                    <Icon
                      icon="building"
                      className="me-1"
                    />
                    {companyName}
                  </div>
                </div>
              )}

            </div>
            <div className="row">
              {medicalDiagnosis
                && (
                  <div className="col-sm-6">
                    <div className="mb-1">
                      <strong className="text-gray">{t('Medical diagnosis')}</strong>
                      <br />
                      <Icon
                        icon="stethoscope"
                        className="me-1"
                      />
                      {medicalDiagnosis}
                    </div>
                  </div>
                )
              }

              {diagnostic
                && (
                  <div className="col-sm-6">
                    <div className="mb-1">
                      <strong className="text-gray">{t('Exam diagnostic')}</strong>
                      <br />
                      <Icon
                        icon="stethoscope"
                        className="me-1"
                      />
                      {diagnostic}
                    </div>
                  </div>
                )
              }
              {observations
                && (
                  <div className="col-sm-6">

                    <div className="mb-1">
                      <strong className="text-gray">{t('Observations')}</strong>
                      <br />
                      <Icon
                        icon="file-magnifying-glass"
                        className="me-1"
                      />
                      {observations}
                    </div>
                  </div>
                )
              }
            </div>
          </div>
          <div className="col-md-3 text-center">
            <StatusDocument status={status} />
            <div className="btn-group-vertical mt-1 w-100">
              <Button
                className="btn btn-primary"
                icon="file-magnifying-glass"
                size="sm"
                onClick={openModalExam}
              >
                {t('See details')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

PatientExamRow.propTypes = {
  id: PropTypes.string.isRequired,
  number: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  patient: PropTypes.object.isRequired,
  dentist: PropTypes.object,
  technician: PropTypes.object,
  radiologist: PropTypes.object,
  medicalCenter: PropTypes.object,
  urgency: PropTypes.string,
  medicalDiagnosis: PropTypes.string,
  observations: PropTypes.string,
  name: PropTypes.string,
  diagnostic: PropTypes.string,
  companyName: PropTypes.string,
  case: PropTypes.object
};

export default PatientExamRow;
